import React, { ReactNode } from 'react';
import styled from 'styled-components/macro';
import {
  ChannelStatusAction,
  MessageType,
  ChannelViewChannelMessageAttachmentFragment,
  ChannelMessageAttachmentType,
  ChannelMessageAttachmentUploadStatus,
  useSubscribeToChannelMessageAttachmentSubscription,
  ChannelViewChannelFragment,
} from 'shared/types/graphql';
import InvoiceAttachment from './Attachments/Invoice';
import FormAttachment from './Attachments/Form';
import VideoAttachment from './Attachments/Video';
import ImageAttachment from './Attachments/Image';
import FileAttachment from './Attachments/File';
import CallRequestAttachment from './Attachments/CallRequest';
import { useAuth } from 'shared/providers/AuthProvider';
import AutomationRun from './Attachments/AutomationRun';
import { HStack } from '@televet/kibble-ui/build/chakra';

interface IMessageBubbleAttachmentProps {
  attachment: ChannelViewChannelMessageAttachmentFragment;
  clinicPetParentId: string;
  channelId: string;
  setPreviewData: (fileUrl: string, attachmentType: ChannelMessageAttachmentType, fileName: string) => void;
  channel?: ChannelViewChannelFragment | null;
  messageType: MessageType;
  setCurrentInvoiceId: (invoiceId: string) => void;
  isLastEligibleMessage: boolean;
}

const MessageBubbleAttachmentContainer = ({ children }: { children: ReactNode }): JSX.Element => {
  return (
    <HStack
      background="background.subtle"
      p={3}
      justify="flex-start"
      border="1px"
      borderColor="border.default"
      borderRadius="md"
    >
      {children}
    </HStack>
  );
};

const MessageBubbleAttachment = ({
  attachment,
  clinicPetParentId,
  channelId,
  setPreviewData,
  channel,
  messageType,
  setCurrentInvoiceId,
  isLastEligibleMessage,
}: IMessageBubbleAttachmentProps): JSX.Element | null => {
  const { attachmentType, entityId, filename, url, id, uploadStatus } = attachment;
  const clinicPetParents = channel?.channelMembers
    .filter((channelMember) => !!channelMember.clinicPetParent)
    .map((channelMember) => channelMember.clinicPetParent);

  useSubscribeToChannelMessageAttachmentSubscription({
    variables: { channelMessageAttachmentId: id },
    skip: attachmentType !== ChannelMessageAttachmentType.Video || !id,
  });

  const { isImpersonating } = useAuth();
  const expandSubmittedForms = true;
  switch (attachmentType) {
    case ChannelMessageAttachmentType.Invoice:
      return (
        <InvoiceAttachment attachment={attachment} channelId={channelId} setCurrentInvoiceId={setCurrentInvoiceId} />
      );
    case ChannelMessageAttachmentType.FormRequest:
      return expandSubmittedForms ? (
        <MessageBubbleAttachmentContainer>
          <FormAttachment
            entityId={entityId}
            clinicPetParentId={clinicPetParentId}
            clinicPetParents={clinicPetParents}
          />
        </MessageBubbleAttachmentContainer>
      ) : (
        <MessageBubbleAttachmentContainer>
          <FormAttachment
            entityId={entityId}
            clinicPetParentId={clinicPetParentId}
            clinicPetParents={clinicPetParents}
          />
        </MessageBubbleAttachmentContainer>
      );
    case ChannelMessageAttachmentType.Image:
      if (url && messageType) {
        return (
          <MessageBubbleContainer messageType={messageType}>
            <MediaPreview data-testid="message-bubble-media-preview">
              <ImageAttachment url={url} filename={filename || Date.now().toString()} setPreviewData={setPreviewData} />
            </MediaPreview>
          </MessageBubbleContainer>
        );
      } else return null;
    case ChannelMessageAttachmentType.Video:
      if (messageType) {
        return (
          <MessageBubbleContainer messageType={messageType}>
            <MediaPreview>
              <VideoAttachment
                url={url || undefined}
                uploadStatus={uploadStatus || ChannelMessageAttachmentUploadStatus.InProgress}
              />
            </MediaPreview>
          </MessageBubbleContainer>
        );
      } else return null;
    case ChannelMessageAttachmentType.File:
      if (url && messageType) {
        return (
          <MessageBubbleAttachmentContainer>
            <MessageBubbleContainer messageType={messageType}>
              <FileAttachment url={url} filename={filename || Date.now().toString()} setPreviewData={setPreviewData} />
            </MessageBubbleContainer>
          </MessageBubbleAttachmentContainer>
        );
      } else return null;
    case ChannelMessageAttachmentType.CallRequest:
      const isJoinCallEnabled =
        isImpersonating ||
        !channel?.channelStatus ||
        channel.channelStatus.channelStatusAction === ChannelStatusAction.Active;
      if (channelId) {
        return (
          <MessageBubbleContainer messageType={messageType}>
            <CallRequestAttachment
              disabled={!isJoinCallEnabled}
              channelId={channelId}
              clinicPetParentId={clinicPetParentId}
            />
          </MessageBubbleContainer>
        );
      } else return null;
    case ChannelMessageAttachmentType.AppointmentConfirmation:
      return null;
    case ChannelMessageAttachmentType.AutomationRun:
      if (!!entityId) {
        return (
          <AutomationRun
            automationRunId={entityId}
            setCurrentInvoiceId={setCurrentInvoiceId}
            isLastEligibleMessage={isLastEligibleMessage}
            useChannelMessageStyles={true}
          />
        );
      } else return null;
    default:
      return null;
  }
};

const MessageBubbleContainer = styled.div<{ messageType: MessageType }>`
  max-width: 100%;
  width: 100%;
  padding-right: 10px;

  & + & {
    margin-top: 4px;
  }
`;

const MediaPreview = styled.div`
  margin: 3px 0;
  img,
  video {
    width: auto;
    max-width: 100%;
    height: 320px;
    border: 1px solid #bbb;
    border-radius: 10px;
    cursor: pointer;
    object-fit: contain;
    z-index: 1;
  }
`;

export default MessageBubbleAttachment;
